import { useCallback, useRef, useReducer, useEffect } from "react";

/**
 * 高度管理 Hook 的配置选项
 */
interface UseItemHeightsOptions<T> {
  /** 动态计算高度的函数 */
  getItemHeight?: (item: T, isExpanded: boolean) => number;
  /** 预估高度，用于初始占位 */
  estimatedItemHeight?: number;
  /** 默认高度（向后兼容） */
  defaultItemHeight?: number;
  /** 默认展开高度（向后兼容） */
  defaultExpandedItemHeight?: number;
}

/**
 * 高度管理 Hook 的返回值
 */
interface UseItemHeightsReturn<T> {
  /** 获取指定项的高度 */
  getHeight: (itemKey: string, item: T, isExpanded: boolean) => number;
  /** 手动更新指定项的高度 */
  updateHeight: (itemKey: string, height: number, isExpanded?: boolean) => void;
  /** 清除所有高度缓存 */
  clearCache: () => void;
  /** 清除指定项的高度缓存 */
  clearItemCache: (itemKey: string) => void;
  /** 强制重新渲染 */
  forceUpdate: () => void;
  /** 版本号，用于触发依赖更新 */
  version: number;
}

/**
 * 高度管理 Hook
 * 用于管理虚拟列表中每个项的高度缓存
 *
 * @param options - 配置选项
 * @returns 高度管理方法
 */
export function useItemHeights<T>(
  options: UseItemHeightsOptions<T>,
): UseItemHeightsReturn<T> {
  const {
    getItemHeight,
    estimatedItemHeight = 164,
    defaultItemHeight,
    defaultExpandedItemHeight,
  } = options;

  // 高度缓存 Map
  const heightCacheRef = useRef<Map<string, number>>(new Map());

  // 用于强制重新渲染，同时作为版本号
  const [version, forceUpdate] = useReducer((x) => x + 1, 0);

  // 使用 ref 存储配置，避免 getHeight 函数频繁重新创建
  const configRef = useRef({
    getItemHeight,
    estimatedItemHeight,
    defaultItemHeight,
    defaultExpandedItemHeight,
  });

  // 更新配置 ref
  useEffect(() => {
    configRef.current = {
      getItemHeight,
      estimatedItemHeight,
      defaultItemHeight,
      defaultExpandedItemHeight,
    };
  }, [getItemHeight, estimatedItemHeight, defaultItemHeight, defaultExpandedItemHeight]);

  /**
   * 获取指定项的高度
   * 优先级：缓存 > 自定义计算函数 > 默认高度（向后兼容） > 预估高度
   */
  const getHeight = useCallback(
    (itemKey: string, item: T, isExpanded: boolean): number => {
      // 1. 构建包含展开状态的缓存键
      const cacheKey = `${itemKey}-${isExpanded ? "expanded" : "collapsed"}`;

      // 2. 先查缓存
      const cachedHeight = heightCacheRef.current.get(cacheKey);
      if (cachedHeight !== undefined) {
        return cachedHeight;
      }

      const config = configRef.current;
      let calculatedHeight: number;

      // 3. 使用自定义计算函数
      if (config.getItemHeight) {
        calculatedHeight = config.getItemHeight(item, isExpanded);
      }
      // 4. 回退到默认高度（向后兼容）
      else if (config.defaultItemHeight !== undefined) {
        calculatedHeight = isExpanded
          ? (config.defaultExpandedItemHeight ?? config.defaultItemHeight * 2)
          : config.defaultItemHeight;
      }
      // 5. 使用预估高度
      else {
        calculatedHeight = config.estimatedItemHeight;
      }

      // 缓存计算结果（使用包含展开状态的键）
      heightCacheRef.current.set(cacheKey, calculatedHeight);

      return calculatedHeight;
    },
    [], // 空依赖数组，函数永远不会重新创建
  );

  /**
   * 手动更新指定项的高度
   * 用于处理流式内容等动态高度场景
   * @param itemKey - 项目键
   * @param height - 新高度
   * @param isExpanded - 是否展开状态，默认为 true（流式内容通常是展开的）
   */
  const updateHeight = useCallback(
    (itemKey: string, height: number, isExpanded = true) => {
      // 构建包含展开状态的缓存键
      const cacheKey = `${itemKey}-${isExpanded ? "expanded" : "collapsed"}`;
      const currentHeight = heightCacheRef.current.get(cacheKey);

      // 使用阈值来避免微小的高度变化导致无限循环
      // 只有当高度变化超过 2px 时才更新
      const threshold = 2;
      if (currentHeight === undefined || Math.abs(currentHeight - height) > threshold) {
        heightCacheRef.current.set(cacheKey, height);
        forceUpdate();
      }
    },
    [],
  );

  /**
   * 清除所有高度缓存
   */
  const clearCache = useCallback(() => {
    heightCacheRef.current.clear();
  }, []);

  /**
   * 清除指定项的高度缓存
   * 用于流式内容等场景，需要重新计算单个项的高度
   * @param itemKey - 项目键
   */
  const clearItemCache = useCallback((itemKey: string) => {
    // 清除该项的所有缓存（展开和收起状态）
    heightCacheRef.current.delete(`${itemKey}-expanded`);
    heightCacheRef.current.delete(`${itemKey}-collapsed`);
    // 触发重新渲染
    forceUpdate();
  }, []);

  return {
    getHeight,
    updateHeight,
    clearCache,
    clearItemCache,
    forceUpdate,
    version,
  };
}
